import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { LocalizationSettings } from './localization-settings.js';
import { StaffPrivacySettings } from './staff-privacy-settings.js';

export class BusinessSettings extends DataModel {
  static readonly TYPE_ID: string = '243a8bf8436a48e4bf55dc9ed23e9655';
  static readonly SCHEMA_VERSION: number = 6;

  constructor(
    public readonly autoJoinClientsByPhoneNumber: boolean,
    public readonly allowCustomBookingSettings: boolean,
    public readonly allowOpenReservations: boolean,
    public readonly staffPrivacySettings: StaffPrivacySettings,
    public readonly localizationSettings: LocalizationSettings,
  ) {
    super();
  }

  get $typeId(): string {
    return BusinessSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(2, BusinessSettings.SCHEMA_VERSION);
  }

  static empty(): BusinessSettings {
    return new BusinessSettings(
        false,
        false,
        false,
        StaffPrivacySettings.none(),
        LocalizationSettings.none(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessSettings {
    return new BusinessSettings(
        MapUtils.get<boolean>(map, 'autoJoinClientsByPhoneNumber'),
        MapUtils.get<boolean>(map, 'allowCustomBookingSettings'),
        MapUtils.get<boolean>(map, 'allowOpenReservations'),
        context.fromData(MapUtils.get<SerializedData>(map, 'staffPrivacySettings')),
        context.fromData(MapUtils.get<SerializedData>(map, 'localizationSettings')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'autoJoinClientsByPhoneNumber': this.autoJoinClientsByPhoneNumber,
      'allowCustomBookingSettings': this.allowCustomBookingSettings,
      'allowOpenReservations': this.allowOpenReservations,
      'staffPrivacySettings': context.toData(this.staffPrivacySettings),
      'localizationSettings': context.toData(this.localizationSettings),
    };
  }

  clone(): this {
    return new BusinessSettings(
        this.autoJoinClientsByPhoneNumber,
        this.allowCustomBookingSettings,
        this.allowOpenReservations,
        this.staffPrivacySettings.clone(),
        this.localizationSettings.clone(),
    ) as this;
  }

  isSame(other: BusinessSettings): boolean {
    if(this === other) {
      return true;
    }

    return other.autoJoinClientsByPhoneNumber === this.autoJoinClientsByPhoneNumber &&
        other.allowCustomBookingSettings === this.allowCustomBookingSettings &&
        other.staffPrivacySettings === this.staffPrivacySettings &&
        isSameData(other.staffPrivacySettings, this.staffPrivacySettings) &&
        isSameData(other.localizationSettings, this.localizationSettings);
  }
}

export class BusinessSettingsBuilder extends DataModelBuilder<BusinessSettings> {
  build(context: SerializationContext, map: SerializedData): BusinessSettings {
    return BusinessSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(BusinessSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BusinessSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 [autoInviteClientsByPhoneNumber] has been renamed to [autoJoinClientsByPhoneNumber]
    if(version < 2) {
      MapUtils.set(map, 'autoJoinClientsByPhoneNumber', MapUtils.get(map, 'autoInviteClientsByPhoneNumber'));
      version = 2;
    }

    // In version 3 [staffPrivacySettings] has been added
    if(version < 3) {
      MapUtils.set(map, 'staffPrivacySettings', context.toData(StaffPrivacySettings.none()));
      version = 3;
    }

    // In version 4 [localizationSettings] has been added
    if(version < 4) {
      MapUtils.set(map, 'localizationSettings', context.toData(LocalizationSettings.none()));
      version = 4;
    }

    // In version 5 [allowCustomBookingSettings] has been added
    if(version < 5) {
      MapUtils.set(map, 'allowCustomBookingSettings', false);
      version = 5;
    }

    // In version 6 [allowOpenReservations] has been added
    if(version < 6) {
      MapUtils.set(map, 'allowOpenReservations', false);
      version = 6;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
